import React from 'react';
import { OfferingCard } from '@ole-ui/ole-ui-components';
import { LearningPath, LiveEvent } from 'types';
import { PremiumSessionUserFacingStatus } from 'types/premiumSession';
import * as Sentry from '@sentry/react';
import { CourseLvcUserFacingStatus } from 'views/Catalog/useCourseLiveClassInfo';
import classNames from 'classnames';
import { handleError } from 'services/ErrorService';
import { OfferingCardContextProvider } from './OfferingCardContext';
import HeaderBuilder from './HeaderBuilder';
import BodyBuilder from './BodyBuilder';
import FooterBuilder from './FooterBuilder';
import OfferingCardFallback from './Fallback';
import { OfferingCardEntry } from './utils/types';

type OfferingCardBuilderProps = {
  children?: React.ReactNode;
  className?: string;
  entry?: OfferingCardEntry;
  hasLiveVirtualClass?: boolean;
  isLivePremiumSessionCourse?: boolean;
  isLocked?: boolean;
  /**
   * Always use the "View" primary action and open the offering landing page,
   * even when the user has progress.
   */
  isSearch?: boolean;
  learningPath?: LearningPath;
  liveEventInfo?: LiveEvent;
  lvcStatus?: CourseLvcUserFacingStatus;
  premiumSessionSlug?: string;
  premiumSessionStatus?: PremiumSessionUserFacingStatus;
};

const OfferingCardBuilder = ({
  children,
  className,
  entry,
  hasLiveVirtualClass,
  isLivePremiumSessionCourse = false,
  isLocked = false,
  isSearch = false,
  learningPath,
  liveEventInfo,
  lvcStatus,
  premiumSessionSlug,
  premiumSessionStatus,
}: OfferingCardBuilderProps) => (
  <Sentry.ErrorBoundary
    fallback={<OfferingCardFallback />}
    onError={handleError}
  >
    <OfferingCardContextProvider
      entry={entry}
      hasLiveVirtualClass={hasLiveVirtualClass}
      isLivePremiumSessionCourse={isLivePremiumSessionCourse}
      isLocked={isLocked}
      isSearch={isSearch}
      learningPath={learningPath}
      liveEventInfo={liveEventInfo}
      lvcStatus={lvcStatus}
      premiumSessionSlug={premiumSessionSlug}
      premiumSessionStatus={premiumSessionStatus}
    >
      <OfferingCard
        className={classNames('offering-card-builder', className, {
          locked: isLocked,
        })}
      >
        {children}
      </OfferingCard>
    </OfferingCardContextProvider>
  </Sentry.ErrorBoundary>
);
OfferingCardBuilder.Header = HeaderBuilder;
OfferingCardBuilder.Body = BodyBuilder;
OfferingCardBuilder.Footer = FooterBuilder;

export default OfferingCardBuilder;
